//src\PO-Process\components\FilteredPO.jsx

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { memo, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import "../style/agGridReact.css";
import {
  Box
} from "@mui/material";
import OutlinedButton from "../../components/ui/OutlinedButton";
import ContainedButton from "../../components/ui/ContainedButton";
import DynamicColorBadge from "../../components/ui/DynamicColorBadge";
import { columnDesign } from "../../helpers/utils"
import { Toaster } from "../../components/Toaster";
import api from "../../axiosConfig";
import { getUser } from "../../helpers";

const FilteredPO = ({ filteredPOList, filterData, handleApplyFilters }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const gridRef = useRef();
  const [selectedRows, setSelectedRows] = useState([]);
  const [downloading, setDownloading] = useState(false)

  const handleViewReport = () => {
    navigate("/po-report");
  };

  const dateFormatter = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return value;
    return d.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  const columnDefs = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      suppressSizeToFit: true,
      cellClass: 'ag-center-cols-container',
      headerClass: 'ag-center-header',
    },
    columnDesign({ headerName: "Customer", field: "Customer", width: 145 }),
    columnDesign({ headerName: "Type", field: "PO_Type", width: 125 }),
    columnDesign({ headerName: "Outlet Code", field: "Outlet_Code", width: 165 }),
    columnDesign({ headerName: "DC Name", field: "DC_Name", width: 150 }),
    {
      headerName: "PO No",
      field: "PO_Number",
      width: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params) => (
        <OutlinedButton sx={{ width: "100%" }} onClick={() =>
          navigate(
            `/po-details/${encodeURIComponent(params.data?.Filename)}${location.search}`
          )
        }>
          {params.value || "-"}
        </OutlinedButton>
      ),
    },
    columnDesign({
      headerName: "PO Date",
      field: "PO_Date",
      width: 150,
      valueFormatter: (p) => dateFormatter(p.value),
      filterValueGetter: (p) => dateFormatter(p.data?.PO_Date),
    }),

    columnDesign({
      headerName: "Expiry Date",
      field: "PO_Expiry_Date",
      width: 165,
      valueFormatter: (p) => dateFormatter(p.value),
      filterValueGetter: (p) => dateFormatter(p.data?.PO_Expiry_Date),
    }),

    columnDesign({
      headerName: "Expected Delivery",
      field: "Expected_Delivery_Date",
      width: 210,
      valueFormatter: (p) => dateFormatter(p.value),
      filterValueGetter: (p) => dateFormatter(p.data?.Expected_Delivery_Date),
    }),
    {
      headerName: "Status",
      field: "status",
      width: 150,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const status = params.value || "-";

        let color;
        switch (status) {
          // case "Posted to iDrive":
          //   color = "warning";   // Final / done
          //   break;

          case "Downloaded":
            color = "success";      // Intermediate
            break;

          case "Ready to Download":
            color = "info";   // Needs action
            break;

          case "Edits Pending":
            color = "error";     // Problem
            break;

          default:
            color = "default";   // Neutral
        }

        return (
          <DynamicColorBadge content={status} color={color} />
        );
      },
    },
    columnDesign({ headerName: "PO Qty", field: "PO_Quantity", width: 150 }),
    columnDesign({ headerName: "Invoiced Qty", field: "Invoice_Quantity", width: 175 }),
    columnDesign({ headerName: "Invoice No", field: "Invoice_Number", width: 165 })
  ], [navigate, location.search, columnDesign]);

  const handleDownload = async () => {
    try {
      const selectedFilenames = selectedRows.map(row => row.Filename);

      if (selectedFilenames.length === 0) {
        Toaster.warning("Please select at least one PO.")
        return;
      }

      setDownloading(true);

      const query = new URLSearchParams({
        ...(filterData?.startDate && { start: filterData?.startDate }),
        ...(filterData?.endDate && { end: filterData?.endDate }),
        ...(filterData?.selectedCustomer && { customer: filterData?.selectedCustomer.map(encodeURIComponent).join(',') }),
        ...(filterData?.selectedDC && { dc: filterData?.selectedDC.map(encodeURIComponent).join(',') }),
        filenames: selectedFilenames.join(','),
        user
      });

      const downloadUrl = `${api.defaults.baseURL}/download-excel?${query.toString()}`;
      window.location.href = downloadUrl;
      setDownloading(false)
      handleApplyFilters()
    } catch (err) {
      setDownloading(false);

      console.error("Download error:", err?.response || err?.message || err);

      if (err?.response?.status === 404) {
        Toaster.warning("Logging endpoint not found (404). Please check backend route '/api/user/log'.");
      } else if (err?.response?.status === 500) {
        Toaster.error("Server error while logging or downloading.");
      } else {
        Toaster.warning("Failed to download PO. Please try again.");
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginBottom: "16px"
        }}
      >
        <ContainedButton disabled={downloading} onClick={handleDownload}>
          {downloading && <>Downloading...</>}
          {!downloading && <>
            <DownloadIcon
              sx={{
                marginRight: "5px",
              }}
            />
            Download Excel
          </>}
        </ContainedButton>
      </Box>

      <Box
        className="ag-theme-quartz ag-theme-idbg"
        sx={{
          height: 500,
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: 1,
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={filteredPOList}
          columnDefs={columnDefs}
          rowSelection="multiple"
          getRowId={(params) => params.data?.PO_Number || params.data?.Filename}
          onSelectionChanged={() => {
            const selected = gridRef.current.api.getSelectedRows();
            setSelectedRows(selected);
          }}
          pagination={true}
          paginationPageSize={10}
          defaultColDef={{
            resizable: true,
            sortable: true,
            cellStyle: {
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            headerClass: 'ag-center-header',
          }}
          overlayNoRowsTemplate={
            '<span style="padding: 10px; display: block;">No rows to display.</span>'
          }
        />

      </Box>

      {/* View Report Button Under the Table */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "left",
          marginTop: "20px",
        }}
      >
        <ContainedButton onClick={handleViewReport}>
          View Verified Records
        </ContainedButton>
      </Box>

    </>
  );
};

export default memo(FilteredPO);
